import { useState, useEffect, useCallback, useRef } from "react";
import { supabase, getAsesorIdByEmail, getLeads } from "@/lib/supabase";
import { normalizeLeadStage } from "@/lib/lead-stages";
import { useAuth } from "@/context/auth-context";

// Prioridades de notificación
export const PRIORITY = {
  CRITICAL: "critical",
  HIGH: "high",
  MEDIUM: "medium",
  LOW: "low",
};

// Tipos de notificaciones
export const TYPE = {
  MESSAGE: "message",
  FOLLOW_UP: "followUp",
  INACTIVE: "inactive",
  ASSIGNMENT: "assignment",
  STAGE_CHANGE: "stageChange",
  EXAM_READY: "examReady",
  SCHEDULED: "scheduled",
  SUCCESS: "success",
  TASK: "task",
};

// Claves de almacenamiento
const STORAGE_KEYS = {
  READ: "aurora_notifications_read",
  DISMISSED: "aurora_notifications_dismissed",
  SHOWN: "aurora_notifications_shown", // Notificaciones que ya se mostraron (una sola vez)
  LAST_SEEN_PREFIX: "last_seen_",
};

// Tiempo mínimo para considerar inactividad (en días)
const INACTIVITY_THRESHOLD_DAYS = 3;

// ============================================================
// UTILIDADES DE ALMACENAMIENTO
// ============================================================

const getStoredSet = (key) => {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed) : new Set();
  } catch {
    return new Set();
  }
};

const persistSet = (key, set) => {
  if (typeof window === "undefined") return;
  try {
    // Limitar el tamaño del set para evitar problemas de almacenamiento
    const arr = Array.from(set).slice(-500);
    window.localStorage.setItem(key, JSON.stringify(arr));
  } catch {
    // Ignore storage errors
  }
};

const getStoredMap = (key) => {
  if (typeof window === "undefined") return new Map();
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return new Map();
    const parsed = JSON.parse(raw);
    return new Map(Object.entries(parsed));
  } catch {
    return new Map();
  }
};

const persistMap = (key, map) => {
  if (typeof window === "undefined") return;
  try {
    const obj = Object.fromEntries(map);
    window.localStorage.setItem(key, JSON.stringify(obj));
  } catch {
    // Ignore storage errors
  }
};

// ============================================================
// UTILIDADES GENERALES
// ============================================================

const sortNotifications = (notifications) => {
  const priorityWeight = {
    [PRIORITY.CRITICAL]: 0,
    [PRIORITY.HIGH]: 1,
    [PRIORITY.MEDIUM]: 2,
    [PRIORITY.LOW]: 3,
  };

  return [...notifications].sort((a, b) => {
    const diff =
      (priorityWeight[a.priority] ?? 99) - (priorityWeight[b.priority] ?? 99);
    if (diff !== 0) return diff;
    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
  });
};

const safeNowIso = () => new Date().toISOString();

const hasExamTag = (tags = []) => {
  if (!tags) return false;
  if (Array.isArray(tags)) {
    return tags.some((tag) => String(tag).toLowerCase() === "examen");
  }
  try {
    const parsed = JSON.parse(tags);
    if (Array.isArray(parsed)) {
      return parsed.some((tag) => String(tag).toLowerCase() === "examen");
    }
  } catch {
    return String(tags).toLowerCase().includes("examen");
  }
  return false;
};

const getLastSeenDate = (leadId) => {
  if (typeof window === "undefined") return new Date(0);
  try {
    const raw = window.localStorage.getItem(
      `${STORAGE_KEYS.LAST_SEEN_PREFIX}${leadId}`,
    );
    if (!raw) return new Date(0);
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
  } catch {
    return new Date(0);
  }
};

const getNormalizedRole = (user) => {
  const raw =
    user?.rol ||
    user?.role ||
    user?.user_metadata?.role ||
    user?.app_metadata?.role ||
    "";
  return String(raw).trim().toLowerCase();
};

const hasGlobalNotificationScope = (user) => {
  const role = getNormalizedRole(user);
  return Boolean(
    user?.isAdmin ||
    role === "admin" ||
    role === "owner" ||
    role === "supervisor",
  );
};

// ============================================================
// BUILDERS DE NOTIFICACIONES
// ============================================================

/**
 * Notificación de nuevo lead asignado
 * ID estable: assignment-{leadId}
 * Solo se muestra UNA vez cuando el lead es asignado
 */
const buildAssignmentNotification = (lead) => {
  if (!lead?.id) return null;
  return {
    id: `assignment-${lead.id}`,
    leadId: lead.id,
    type: TYPE.ASSIGNMENT,
    priority: PRIORITY.HIGH,
    title: "Nuevo lead asignado",
    message: `${lead.nombre_tutor || "Lead sin nombre"} acaba de llegar a tu bandeja.`,
    data: {
      leadName: lead.nombre_tutor,
      telefono: lead.telefono,
      campus: lead.campus,
    },
    createdAt: lead.created_at || safeNowIso(),
    oneTime: true, // Marcar como notificación de una sola vez
  };
};

/**
 * Notificación de inactividad
 * ID estable: inactive-{leadId} (sin días para evitar duplicados)
 * Solo se muestra UNA vez cuando se detecta inactividad
 */
const buildInactivityNotification = (lead, now) => {
  if (!lead?.id) return null;
  const lastUpdate = new Date(lead.updated_at || lead.created_at || now);
  const diffDays = Math.floor((now - lastUpdate) / (1000 * 60 * 60 * 24));

  if (diffDays < INACTIVITY_THRESHOLD_DAYS) return null;

  return {
    id: `inactive-${lead.id}`, // ID estable sin días
    leadId: lead.id,
    type: TYPE.INACTIVE,
    priority: diffDays >= 5 ? PRIORITY.CRITICAL : PRIORITY.HIGH,
    title: `Lead sin actividad (${diffDays} días)`,
    message: `${lead.nombre_tutor || "Lead"} necesita seguimiento urgente.`,
    data: {
      leadName: lead.nombre_tutor,
      telefono: lead.telefono,
      stage: normalizeLeadStage(lead.estado_lead),
      daysSinceUpdate: diffDays,
    },
    createdAt: safeNowIso(),
    oneTime: true, // Solo mostrar una vez
  };
};

/**
 * Notificación de lead listo para examen
 * ID estable: exam-{leadId}
 */
const buildExamNotification = (lead) => {
  if (!lead?.id) return null;
  const stage = normalizeLeadStage(lead.estado_lead);
  if (stage !== "aplicar_examen" || lead.aplico_examen) return null;

  return {
    id: `exam-${lead.id}`,
    leadId: lead.id,
    type: TYPE.EXAM_READY,
    priority: PRIORITY.HIGH,
    title: "Lead listo para examen",
    message: `${lead.nombre_tutor || "Lead"} está preparado para presentar examen.`,
    data: {
      leadName: lead.nombre_tutor,
      telefono: lead.telefono,
      nivel: lead.nivel_educativo,
    },
    createdAt: safeNowIso(),
    oneTime: true,
  };
};

/**
 * Notificación de seguimiento programado
 * ID estable: followup-{leadId}-{fecha} para ese día específico
 */
const buildFollowUpNotification = (lead, now) => {
  if (!lead?.id || !lead?.fecha_cita) return null;

  const followUpDate = new Date(lead.fecha_cita);
  const diffMs = followUpDate - now;

  // Solo mostrar si está dentro de las próximas 24h o vencido hasta 48h
  if (diffMs > 24 * 60 * 60 * 1000 || diffMs < -48 * 60 * 60 * 1000)
    return null;

  const isPast = diffMs < 0;
  const dateKey = followUpDate.toISOString().split("T")[0]; // Usar solo la fecha

  return {
    id: `followup-${lead.id}-${dateKey}`,
    leadId: lead.id,
    type: TYPE.SCHEDULED,
    priority: isPast ? PRIORITY.CRITICAL : PRIORITY.HIGH,
    title: isPast ? "Seguimiento vencido" : "Seguimiento para hoy",
    message: `${lead.nombre_tutor || "Lead"} ${
      isPast ? "tiene seguimiento pendiente" : "requiere seguimiento hoy"
    }.`,
    data: {
      leadName: lead.nombre_tutor,
      telefono: lead.telefono,
      followUpDate: lead.fecha_cita,
      isPast,
    },
    createdAt: safeNowIso(),
    oneTime: true,
  };
};

/**
 * Notificación: prospecto solicitó cita (IA detectó interés; asesora debe tomar control y agendar)
 * ID estable: cita-solicitada-{leadId}
 */
const buildCitaSolicitadaNotification = (lead) => {
  if (!lead?.id) return null;
  const dbStatus = String(lead.estado_lead || "").toLowerCase();
  if (dbStatus !== "cita_solicitada") return null;

  return {
    id: `cita-solicitada-${lead.id}`,
    leadId: lead.id,
    type: TYPE.SCHEDULED,
    priority: PRIORITY.CRITICAL,
    title: "Solicita cita",
    message: `${lead.nombre_tutor || "Prospecto"} quiere agendar visita. Toma control y asigna día/hora.`,
    data: {
      leadName: lead.nombre_tutor,
      telefono: lead.telefono,
      campus: lead.campus,
    },
    createdAt: lead.updated_at || lead.created_at || safeNowIso(),
    oneTime: false,
    forceUnread: true,
  };
};

/**
 * Notificación de tarea vencida o próxima
 * ID estable: task-{taskId}-{stateKey} donde stateKey cambia si pasa de "próxima" a "vencida"
 * Se dispara en 3 casos:
 *   - Vencida (fecha < now)
 *   - Hoy (mismo día pero en el futuro)
 *   - Próxima hora (cita en menos de 1h)
 */
const buildTaskNotification = (task, now) => {
  if (!task?.id || task.completada) return null;
  if (!task.fecha_vencimiento) return null;

  const dueDate = new Date(task.fecha_vencimiento);
  if (Number.isNaN(dueDate.getTime())) return null;

  const diffMs = dueDate - now;
  const diffH = diffMs / (1000 * 60 * 60);
  const sameDay = dueDate.toDateString() === now.toDateString();

  // Solo notificar si es relevante: vencida, hoy, o cita dentro de 1h
  let state = null;
  let priority = PRIORITY.MEDIUM;
  let title = "";
  if (diffMs < 0) {
    state = "overdue";
    priority = PRIORITY.CRITICAL;
    title = `Tarea vencida: ${task.titulo}`;
  } else if (diffH <= 1) {
    state = "soon";
    priority = PRIORITY.CRITICAL;
    title = `En menos de 1h: ${task.titulo}`;
  } else if (sameDay) {
    state = "today";
    priority = PRIORITY.HIGH;
    title = `Hoy: ${task.titulo}`;
  } else {
    return null;
  }

  const leadName = task.lead?.nombre_tutor || task.lead_nombre || "Sin prospecto";
  const horaStr = dueDate.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });

  return {
    id: `task-${task.id}-${state}`,
    leadId: task.lead_id || null,
    type: TYPE.TASK,
    priority,
    title,
    message: `${leadName} · ${horaStr}`,
    data: {
      taskId: task.id,
      leadName,
      telefono: task.lead?.telefono || null,
      fechaVencimiento: task.fecha_vencimiento,
      tipo: task.tipo,
    },
    createdAt: safeNowIso(),
    oneTime: false,
    forceUnread: state === "overdue" || state === "soon",
  };
};

/**
 * Notificación de lead valioso sin cerrar
 * ID estable: valuable-{leadId}
 */
const buildValuableNotification = (lead) => {
  if (!lead?.id) return null;
  const stage = normalizeLeadStage(lead.estado_lead);
  if (
    stage === "entrega_resultado" ||
    stage === "pendiente_pago" ||
    stage === "inscrito" ||
    stage === "archivado"
  )
    return null;
  if (!hasExamTag(lead.tags) && !lead.aplico_examen) return null;

  return {
    id: `valuable-${lead.id}`,
    leadId: lead.id,
    type: TYPE.FOLLOW_UP,
    priority: PRIORITY.HIGH,
    title: "Lead valioso sin cerrar",
    message: `${lead.nombre_tutor || "Lead"} aplicó examen, prioriza el cierre.`,
    data: {
      leadName: lead.nombre_tutor,
      telefono: lead.telefono,
      stage,
    },
    createdAt: safeNowIso(),
    oneTime: true,
  };
};

// ============================================================
// NOTIFICACIONES NATIVAS DEL NAVEGADOR
// ============================================================

const requestBrowserPermission = () => {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission === "default") {
    Notification.requestPermission();
  }
};

const sendBrowserNotification = (title, body, options = {}) => {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  // No enviar si la pestaña está activa y visible
  if (document.visibilityState === "visible") return;

  try {
    const n = new Notification(title, {
      body,
      icon: "/tepeyac.png",
      badge: "/tepeyac.png",
      tag: options.tag || "crm-tepeyac",
      renotify: true,
      ...options,
    });
    // Cerrar automáticamente después de 6 segundos
    setTimeout(() => n.close(), 6000);
  } catch {
    // Silenciar errores de notificación
  }
};

// ============================================================
// HOOK PRINCIPAL
// ============================================================

export function useNotifications(leadId) {
  const { user } = useAuth();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [playSoundType, setPlaySoundType] = useState(null);

  // Solicitar permiso de notificaciones al montar
  useEffect(() => {
    if (user) requestBrowserPermission();
  }, [user]);

  // Referencias
  const unreadCountRef = useRef(0);
  const lastSoundTime = useRef(0);
  const assignedLeadIdsRef = useRef(new Set());
  const asesorIdRef = useRef();
  const readIdsRef = useRef(getStoredSet(STORAGE_KEYS.READ));
  const dismissedIdsRef = useRef(getStoredSet(STORAGE_KEYS.DISMISSED));
  const shownIdsRef = useRef(getStoredSet(STORAGE_KEYS.SHOWN)); // Notificaciones ya mostradas
  const notificationsRef = useRef([]);
  const leadCacheRef = useRef(new Map());
  const unreadByLeadRef = useRef(new Map());
  const loadInFlightRef = useRef(false);
  const pendingLoadOptionsRef = useRef(null);
  const initialLoadDoneRef = useRef(false);

  const userEmail = (user?.email || "").toLowerCase();
  const allowGlobalScope = hasGlobalNotificationScope(user);

  const resolveAsesorId = useCallback(async () => {
    if (!userEmail) return null;
    if (asesorIdRef.current !== undefined) return asesorIdRef.current;
    const id = await getAsesorIdByEmail(userEmail);
    asesorIdRef.current = id ?? null;
    return asesorIdRef.current;
  }, [userEmail]);

  useEffect(() => {
    asesorIdRef.current = undefined;
  }, [userEmail]);

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  // ============================================================
  // FUNCIONES DE AUDIO Y SINCRONIZACIÓN
  // ============================================================

  const playSound = useCallback((type) => {
    if (!type) return;
    const now = Date.now();
    if (now - lastSoundTime.current < 4000) return;
    setPlaySoundType(type);
    lastSoundTime.current = now;
  }, []);

  const emitUnreadSync = useCallback((leadId, count) => {
    if (!leadId) return;
    unreadByLeadRef.current.set(leadId, count);
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("leadUnreadSync", {
          detail: { leadId, count },
        }),
      );
    }
  }, []);

  const commitNotifications = useCallback(
    (updater, soundHint = null) => {
      setNotifications((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        const sorted = sortNotifications(next);
        const unread = sorted.filter((n) => !n.read).length;
        const previousUnread = unreadCountRef.current;
        unreadCountRef.current = unread;
        setUnreadCount(unread);

        // Solo reproducir sonido si hay nuevas no leídas y ya pasó la carga inicial
        if (soundHint && initialLoadDoneRef.current) {
          playSound(soundHint);
        } else if (unread > previousUnread && initialLoadDoneRef.current) {
          playSound("default");
        }

        // Notificación nativa del navegador cuando hay nuevas no leídas
        if (unread > previousUnread && initialLoadDoneRef.current) {
          const prevIds = new Set(prev.map((n) => n.id));
          const newNotifications = sorted.filter(
            (n) => !n.read && !prevIds.has(n.id),
          );
          if (newNotifications.length > 0) {
            const newest = newNotifications[0];
            sendBrowserNotification(newest.title, newest.message, {
              tag: `crm-${newest.id}`,
            });
          } else if (unread > previousUnread) {
            // Notificación genérica si incrementó pero no hay nuevos IDs (ej: mensaje actualizado)
            sendBrowserNotification(
              "CRM Tepeyac",
              `Tienes ${unread} notificación${unread > 1 ? "es" : ""} sin leer`,
            );
          }
        }

        return sorted;
      });
    },
    [playSound],
  );

  // ============================================================
  // CONSTRUCCIÓN DE NOTIFICACIONES DE MENSAJES
  // ============================================================

  const buildMessageNotifications = useCallback(
    async (leads) => {
      const leadMap = new Map();
      const lastSeenMap = new Map();
      const leadIds = [];

      leads.forEach((lead) => {
        if (!lead?.id) return;
        leadIds.push(lead.id);
        leadMap.set(lead.id, lead);
        lastSeenMap.set(lead.id, getLastSeenDate(lead.id));
      });

      if (!leadIds.length) return [];

      const earliestSeen = Math.min(
        ...Array.from(lastSeenMap.values()).map((date) =>
          date instanceof Date ? date.getTime() : new Date(0).getTime(),
        ),
      );
      const minCreatedAt = Number.isFinite(earliestSeen)
        ? new Date(earliestSeen).toISOString()
        : new Date(0).toISOString();

      const { data: rows, error } = await supabase
        .from("messages")
        .select("id, lead_id, body, created_at")
        .in("lead_id", leadIds)
        .eq("direction", "in")
        .gt("created_at", minCreatedAt)
        .order("created_at", { ascending: false });

      if (error || !rows || rows.length === 0) return [];

      const grouped = new Map();
      const countsMap = new Map();

      for (const row of rows) {
        const leadId = row.lead_id;
        if (!leadId || !lastSeenMap.has(leadId)) continue;
        const lastSeen = lastSeenMap.get(leadId) || new Date(0);
        const createdAt = new Date(row.created_at);
        if (createdAt <= lastSeen) continue;

        const bucket = grouped.get(leadId) || [];
        const updatedCount = (countsMap.get(leadId) || 0) + 1;
        countsMap.set(leadId, updatedCount);

        if (bucket.length < 3) {
          bucket.push(row);
          grouped.set(leadId, bucket);
        }
      }

      const results = [];

      grouped.forEach((messages, leadId) => {
        const lead = leadMap.get(leadId) || {};
        const count = countsMap.get(leadId) || messages.length;
        emitUnreadSync(leadId, count);

        // Usar el ID del mensaje más reciente para que al descartarlo,
        // un nuevo mensaje futuro genere una notificación diferente
        const latestMsgId = messages[0]?.id || "unknown";
        results.push({
          id: `message-${leadId}-${latestMsgId}`,
          leadId,
          type: TYPE.MESSAGE,
          priority: count >= 3 ? PRIORITY.CRITICAL : PRIORITY.HIGH,
          title: count > 1 ? `${count} mensajes sin leer` : "Nuevo mensaje",
          message: `${lead.nombre_tutor || "Lead"}: ${messages[0].body || "Mensaje nuevo"}`,
          data: {
            leadName: lead.nombre_tutor,
            telefono: lead.telefono,
            count,
          },
          createdAt: messages[0].created_at,
          forceUnread: true,
          oneTime: false,
        });
      });

      // Emitir 0 para leads sin mensajes nuevos
      const zeroCountLeads = leadIds.filter((id) => !countsMap.has(id));
      zeroCountLeads.forEach((leadId) => emitUnreadSync(leadId, 0));

      return results;
    },
    [emitUnreadSync],
  );

  // ============================================================
  // CARGA DE NOTIFICACIONES
  // ============================================================

  const loadNotifications = useCallback(
    async function loadNotificationsInner({ silent = false } = {}) {
      if (!user) {
        if (!silent) setLoading(false);
        return;
      }

      if (loadInFlightRef.current) {
        pendingLoadOptionsRef.current = { silent: true };
        return;
      }

      loadInFlightRef.current = true;
      if (!silent) setLoading(true);

      let soundHint = null;

      try {
        let assignedLeads = [];

        if (allowGlobalScope) {
          const { data: leads, error } = await supabase
            .from("leads")
            .select("*")
            .order("updated_at", { ascending: false });
          if (error) throw error;
          assignedLeads = leads || [];
        } else if (userEmail) {
          const { data: leads, error } = await getLeads(userEmail);
          if (error) throw error;
          assignedLeads = leads || [];
        } else {
          commitNotifications(() => []);
          return;
        }
        const collectionMap = new Map();
        const now = new Date();

        assignedLeadIdsRef.current = new Set(
          assignedLeads.map((lead) => lead.id).filter(Boolean),
        );

        const leadCache = new Map();
        assignedLeads.forEach((lead) => {
          if (lead?.id) leadCache.set(lead.id, lead);
        });
        leadCacheRef.current = leadCache;

        /**
         * Registra una notificación, respetando las reglas de:
         * - No mostrar si está descartada
         * - No mostrar si ya fue mostrada (oneTime) y está en shownIdsRef
         * - No mostrar si ya fue leída y no tiene forceUnread
         * - Mantener estado de lectura
         */
        const registerNotification = (notification) => {
          if (!notification || !notification.id) return;

          // Si está descartada, no mostrar
          if (dismissedIdsRef.current.has(notification.id)) {
            return;
          }

          // Si es oneTime y ya se mostró antes, no mostrar de nuevo
          if (
            notification.oneTime &&
            shownIdsRef.current.has(notification.id)
          ) {
            return;
          }

          // Si ya está marcada como leída y no tiene forceUnread, no mostrar de nuevo
          if (
            readIdsRef.current.has(notification.id) &&
            !notification.forceUnread
          ) {
            // Si ya fue leída, marcarla también como mostrada para evitar que vuelva a aparecer
            if (notification.oneTime) {
              shownIdsRef.current.add(notification.id);
            }
            return;
          }

          // Si ya existe en la colección actual y no tiene forceUnread, no duplicar
          if (collectionMap.has(notification.id) && !notification.forceUnread) {
            return;
          }

          // Determinar si es nueva (para sonido)
          const isNew =
            !shownIdsRef.current.has(notification.id) &&
            !notificationsRef.current.some((n) => n.id === notification.id);

          if (isNew && !notification.read) {
            if (notification.priority === PRIORITY.CRITICAL) {
              soundHint = soundHint || "urgent";
            } else if (notification.type === TYPE.MESSAGE) {
              soundHint = soundHint || "message";
            }
          }

          // Marcar como mostrada si es oneTime
          if (notification.oneTime) {
            shownIdsRef.current.add(notification.id);
          }

          const readFromStore =
            readIdsRef.current.has(notification.id) &&
            !notification.forceUnread;

          collectionMap.set(notification.id, {
            ...notification,
            read: readFromStore,
            createdAt: notification.createdAt || safeNowIso(),
          });
        };

        // Procesar todos los leads
        for (const lead of assignedLeads) {
          if (!lead?.id) continue;

          // Notificación de asignación (solo para leads nuevos no vistos)
          registerNotification(buildAssignmentNotification(lead));

          // Notificación de inactividad
          registerNotification(buildInactivityNotification(lead, now));

          // Notificación de examen
          registerNotification(buildExamNotification(lead));

          // Notificación de seguimiento
          registerNotification(buildFollowUpNotification(lead, now));

          // Notificación de lead valioso
          registerNotification(buildValuableNotification(lead));

          // Notificación: prospecto solicitó cita (prioridad alta para asesoras)
          registerNotification(buildCitaSolicitadaNotification(lead));
        }

        // Notificaciones de mensajes (estas siempre se procesan)
        const messageNotifications =
          await buildMessageNotifications(assignedLeads);
        messageNotifications.forEach((notification) =>
          registerNotification(notification),
        );

        // Notificaciones de tareas (vencidas, hoy, próximas)
        try {
          const asesorIdForTasks =
            !allowGlobalScope && userEmail ? await resolveAsesorId() : null;
          let tasksQuery = supabase
            .from("tasks")
            .select(
              "id, lead_id, asesor_id, titulo, tipo, prioridad, fecha_vencimiento, completada, lead:lead_id(id, nombre_tutor, telefono)"
            )
            .eq("completada", false)
            .not("fecha_vencimiento", "is", null);
          if (!allowGlobalScope && asesorIdForTasks) {
            tasksQuery = tasksQuery.eq("asesor_id", asesorIdForTasks);
          }
          const { data: tasks, error: tasksError } = await tasksQuery;
          if (!tasksError && Array.isArray(tasks)) {
            for (const t of tasks) {
              registerNotification(buildTaskNotification(t, now));
            }
          }
        } catch (taskErr) {
          console.warn("[notifications] task builder error:", taskErr?.message);
        }

        // Persistir las notificaciones mostradas
        persistSet(STORAGE_KEYS.SHOWN, shownIdsRef.current);

        const collection = Array.from(collectionMap.values());
        commitNotifications(() => collection, soundHint);

        // Marcar que ya se hizo la carga inicial
        initialLoadDoneRef.current = true;
      } catch (error) {
        console.error("[notifications] error loading", error);
      } finally {
        if (!silent) setLoading(false);
        loadInFlightRef.current = false;

        if (pendingLoadOptionsRef.current) {
          const next = pendingLoadOptionsRef.current;
          pendingLoadOptionsRef.current = null;
          loadNotificationsInner(next);
        }
      }
    },
    [
      allowGlobalScope,
      buildMessageNotifications,
      commitNotifications,
      resolveAsesorId,
      user,
      userEmail,
    ],
  );

  // ============================================================
  // MANEJO DE MENSAJES EN TIEMPO REAL
  // ============================================================

  const handleRealtimeMessage = useCallback(
    (payload) => {
      const leadId = payload?.new?.lead_id;
      if (!leadId) return;
      if (!assignedLeadIdsRef.current.has(leadId)) return;

      const createdAtRaw = payload?.new?.created_at;
      const createdAt = createdAtRaw ? new Date(createdAtRaw) : new Date();
      const lastSeen = getLastSeenDate(leadId);

      if (createdAt <= lastSeen) {
        emitUnreadSync(leadId, 0);
        return;
      }

      const nextCount = (unreadByLeadRef.current.get(leadId) || 0) + 1;
      emitUnreadSync(leadId, nextCount);

      const lead = leadCacheRef.current.get(leadId) || {};
      const messageBody = payload?.new?.body || "Mensaje nuevo";

      const notification = {
        id: `message-${leadId}`,
        leadId,
        type: TYPE.MESSAGE,
        priority: nextCount >= 3 ? PRIORITY.CRITICAL : PRIORITY.HIGH,
        title:
          nextCount > 1
            ? `${nextCount} mensajes sin leer`
            : `Nuevo mensaje de ${lead.nombre_tutor || "Lead"}`,
        message: `${lead.nombre_tutor || "Lead"}: ${messageBody}`,
        data: {
          leadName: lead.nombre_tutor,
          telefono: lead.telefono,
          count: nextCount,
        },
        createdAt: payload?.new?.created_at || safeNowIso(),
        forceUnread: true,
      };

      // Notificación nativa del navegador para mensajes entrantes
      sendBrowserNotification(
        notification.title,
        notification.message,
        { tag: `crm-msg-${leadId}` },
      );

      commitNotifications((prev) => {
        const filtered = prev.filter((n) => n.id !== notification.id);
        return [...filtered, notification];
      }, "message");
    },
    [commitNotifications, emitUnreadSync],
  );

  // ============================================================
  // EFECTOS
  // ============================================================

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      assignedLeadIdsRef.current.clear();
      initialLoadDoneRef.current = false;
      return;
    }

    let isMounted = true;

    loadNotifications({ silent: false });

    let leadsChannel = null;
    let messagesChannel = null;
    let tasksChannel = null;
    let taskTickInterval = null;

    (async () => {
      const asesorId =
        !allowGlobalScope && userEmail ? await resolveAsesorId() : null;

      const leadsFilter =
        !allowGlobalScope && asesorId
          ? { filter: `asesor_id=eq.${asesorId}` }
          : {};

      // Suscripcion a cambios en leads
      leadsChannel = supabase
        .channel(`notifications-leads-${userEmail || "anon"}`)
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "leads",
            ...leadsFilter,
          },
          () => loadNotifications({ silent: true }),
        )
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "leads",
            ...leadsFilter,
          },
          () => {
            if (!isMounted) return;
            loadNotifications({ silent: true });
          },
        )
        .subscribe();

      // Suscripcion a mensajes nuevos
      messagesChannel = supabase
        .channel(`notifications-messages-${userEmail || "anon"}`)
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "messages",
            filter: "direction=eq.in",
          },
          (payload) => {
            if (!isMounted) return;
            handleRealtimeMessage(payload);
          },
        )
        .subscribe();

      // Suscripcion a tareas (nuevas o cambios)
      tasksChannel = supabase
        .channel(`notifications-tasks-${userEmail || "anon"}`)
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "tasks" },
          () => {
            if (!isMounted) return;
            loadNotifications({ silent: true });
          },
        )
        .subscribe();

      // Revisar estado de tareas cada 2 minutos (para que pasen de "hoy" → "vencida")
      taskTickInterval = setInterval(() => {
        if (!isMounted) return;
        loadNotifications({ silent: true });
      }, 120000);
    })();

    return () => {
      isMounted = false;
      if (leadsChannel) supabase.removeChannel(leadsChannel);
      if (messagesChannel) supabase.removeChannel(messagesChannel);
      if (tasksChannel) supabase.removeChannel(tasksChannel);
      if (taskTickInterval) clearInterval(taskTickInterval);
      assignedLeadIdsRef.current.clear();
    };
  }, [
    allowGlobalScope,
    handleRealtimeMessage,
    loadNotifications,
    resolveAsesorId,
    user,
    userEmail,
  ]);

  // Limpiar estado de sonido
  useEffect(() => {
    if (playSoundType) {
      const timer = setTimeout(() => setPlaySoundType(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [playSoundType]);

  // ============================================================
  // ACCIONES DEL USUARIO
  // ============================================================

  const markAsRead = useCallback(
    (notificationId) => {
      if (!notificationId) return;

      readIdsRef.current.add(notificationId);
      const notification = notificationsRef.current.find(
        (n) => n.id === notificationId,
      );
      // Si es oneTime, marcar como mostrada
      if (notification?.oneTime) {
        shownIdsRef.current.add(notificationId);
        persistSet(STORAGE_KEYS.SHOWN, shownIdsRef.current);
      }
      // Si tiene forceUnread, también descartar para que no reaparezca al recargar
      if (notification?.forceUnread) {
        dismissedIdsRef.current.add(notificationId);
        persistSet(STORAGE_KEYS.DISMISSED, dismissedIdsRef.current);
      }
      persistSet(STORAGE_KEYS.READ, readIdsRef.current);

      commitNotifications((prev) => {
        let leadId = null;
        const next = prev.filter((n) => {
          if (n.id === notificationId) {
            leadId = n.leadId || null;
            return false;
          }
          return true;
        });
        if (leadId) emitUnreadSync(leadId, 0);
        return next;
      });
    },
    [commitNotifications, emitUnreadSync],
  );

  const markAllAsRead = useCallback(() => {
    const current = notificationsRef.current || [];

    current.forEach((n) => {
      readIdsRef.current.add(n.id);
      if (n.oneTime) {
        shownIdsRef.current.add(n.id);
      }
      // Descartar forceUnread para que no reaparezcan al recargar
      if (n.forceUnread) {
        dismissedIdsRef.current.add(n.id);
      }
      if (n.leadId) emitUnreadSync(n.leadId, 0);
    });

    persistSet(STORAGE_KEYS.READ, readIdsRef.current);
    persistSet(STORAGE_KEYS.SHOWN, shownIdsRef.current);
    persistSet(STORAGE_KEYS.DISMISSED, dismissedIdsRef.current);
    commitNotifications(() => []);
    playSound("success");
  }, [commitNotifications, emitUnreadSync, playSound]);

  const dismissNotification = useCallback(
    (notificationId) => {
      if (!notificationId) return;

      dismissedIdsRef.current.add(notificationId);
      readIdsRef.current.delete(notificationId);

      persistSet(STORAGE_KEYS.DISMISSED, dismissedIdsRef.current);
      persistSet(STORAGE_KEYS.READ, readIdsRef.current);

      commitNotifications((prev) => {
        let leadId = null;
        const next = prev.filter((n) => {
          if (n.id === notificationId) {
            leadId = n.leadId || null;
            return false;
          }
          return true;
        });
        if (leadId) emitUnreadSync(leadId, 0);
        return next;
      });
    },
    [commitNotifications, emitUnreadSync],
  );

  const addNotification = useCallback(
    (notification) => {
      if (!notification || !notification.id) return;

      commitNotifications(
        (prev) => {
          const exists = prev.some((n) => n.id === notification.id);
          if (exists) return prev;
          return [
            ...prev,
            {
              ...notification,
              read: false,
              createdAt: notification.createdAt || safeNowIso(),
            },
          ];
        },
        notification.priority === PRIORITY.CRITICAL
          ? "urgent"
          : notification.type === TYPE.MESSAGE
            ? "message"
            : "default",
      );
    },
    [commitNotifications],
  );

  /**
   * Limpia todas las notificaciones mostradas (para desarrollo/testing)
   */
  const clearShownNotifications = useCallback(() => {
    shownIdsRef.current.clear();
    readIdsRef.current.clear();
    dismissedIdsRef.current.clear();
    persistSet(STORAGE_KEYS.SHOWN, shownIdsRef.current);
    persistSet(STORAGE_KEYS.READ, readIdsRef.current);
    persistSet(STORAGE_KEYS.DISMISSED, dismissedIdsRef.current);
    loadNotifications({ silent: false });
  }, [loadNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    playSoundType,
    addNotification,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    clearShownNotifications, // Útil para desarrollo
  };
}
